import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Landing from "./pages/Landing";
import About from "./pages/About";
import Subjects from "./pages/Subjects";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ChangePassword from "./pages/ChangePassword";
import StudentDashboard from "./pages/student/Dashboard";
import QuestionBank from "./pages/student/QuestionBank";
import Quiz from "./pages/student/Quiz";
import AITutor from "./pages/student/AITutor";
import BookLesson from "./pages/student/BookLesson";
import StudentAnalytics from "./pages/student/Analytics";
import ParentDashboard from "./pages/parent/Dashboard";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminSignups from "./pages/admin/Signups";
import AdminStudents from "./pages/admin/Students";
import AdminBookings from "./pages/admin/Bookings";
import AdminQuestions from "./pages/admin/Questions";
import AdminBank from "./pages/admin/Bank";
import AdminAssignments from "./pages/admin/Assignments";
import AdminWorksheets from "./pages/admin/Worksheets";
import AdminProgrammes from "./pages/admin/Programmes";
import AdminGroups from "./pages/admin/Groups";
import AdminRequests from "./pages/admin/Requests";
import AdminProgress from "./pages/admin/Progress";
import AdminStar from "./pages/admin/StarOfTheMonth";
import AdminPrintTest from "./pages/admin/PrintTest";
import StudentAssignments from "./pages/student/Assignments";
import Upload from "./pages/Upload";
import AdminBlog from "./pages/admin/Blog";
import AdminTestimonials from "./pages/admin/Testimonials";
import AdminEnquiries from "./pages/admin/Enquiries";
import "./App.css";

const Protected = ({ role, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center brand-navy">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // An account whose password was issued by the tutor sets its own before
  // anything else. Rendered in place rather than redirected to, so there is no
  // address to bookmark past and no route that has to be excluded from itself.
  // The API refuses the same account anyway (`get_current_user`); this is the
  // half of it that explains itself.
  if (user.must_change_password) return <ChangePassword forced />;
  if (role && user.role !== role) {
    if (user.role === "tutor") return <Navigate to="/admin" replace />;
    if (user.role === "parent") return <Navigate to="/parent" replace />;
    return <Navigate to="/student" replace />;
  }
  return children;
};

/**
 * Student pages switched off for now, by the tutor's decision (24 Oct 2026).
 *
 * The practice quiz runs on invented multiple-choice seed data rather than the
 * real question bank; Book a Lesson and the Question Bank are not part of how
 * work is set; and the AI tutor bills the practice's own Anthropic API key per
 * message, which nobody had agreed to pay for.
 *
 * **Hiding the menu entry is not enough.** A page nobody links to is unlisted,
 * not off, and a child who has bookmarked it carries on as before - so the
 * route redirects as well. `PortalShell` carries the matching `off` flags;
 * change both together or the menu and the routes disagree.
 */
const STUDENT_PAGES_OFF = [
  "/student/questions",
  "/student/quiz",
  "/student/tutor",
  "/student/book",
];

const studentPage = (path, element) =>
  STUDENT_PAGES_OFF.includes(path)
    ? <Navigate to="/student" replace />
    : <Protected role="student">{element}</Protected>;

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/gcse" element={<Subjects />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/upload/:token" element={<Upload />} />

          <Route path="/student" element={<Protected role="student"><StudentDashboard /></Protected>} />
          <Route path="/student/questions" element={studentPage("/student/questions", <QuestionBank />)} />
          <Route path="/student/assignments" element={<Protected role="student"><StudentAssignments /></Protected>} />
          <Route path="/student/quiz" element={studentPage("/student/quiz", <Quiz />)} />
          <Route path="/student/tutor" element={studentPage("/student/tutor", <AITutor />)} />
          <Route path="/student/book" element={studentPage("/student/book", <BookLesson />)} />
          <Route path="/student/analytics" element={<Protected role="student"><StudentAnalytics /></Protected>} />

          <Route path="/parent" element={<Protected role="parent"><ParentDashboard /></Protected>} />

          <Route path="/admin" element={<Protected role="tutor"><AdminDashboard /></Protected>} />
          <Route path="/admin/signups" element={<Protected role="tutor"><AdminSignups /></Protected>} />
          <Route path="/admin/students" element={<Protected role="tutor"><AdminStudents /></Protected>} />
          <Route path="/admin/bookings" element={<Protected role="tutor"><AdminBookings /></Protected>} />
          <Route path="/admin/questions" element={<Protected role="tutor"><AdminQuestions /></Protected>} />
          <Route path="/admin/bank" element={<Protected role="tutor"><AdminBank /></Protected>} />
          <Route path="/admin/worksheets" element={<Protected role="tutor"><AdminWorksheets /></Protected>} />
          <Route path="/admin/programmes" element={<Protected role="tutor"><AdminProgrammes /></Protected>} />
          <Route path="/admin/groups" element={<Protected role="tutor"><AdminGroups /></Protected>} />
          <Route path="/admin/assignments" element={<Protected role="tutor"><AdminAssignments /></Protected>} />
          <Route path="/admin/requests" element={<Protected role="tutor"><AdminRequests /></Protected>} />
          <Route path="/admin/progress" element={<Protected role="tutor"><AdminProgress /></Protected>} />
          <Route path="/admin/star" element={<Protected role="tutor"><AdminStar /></Protected>} />
          <Route path="/admin/print" element={<Protected role="tutor"><AdminPrintTest /></Protected>} />
          <Route path="/admin/blog" element={<Protected role="tutor"><AdminBlog /></Protected>} />
          <Route path="/admin/testimonials" element={<Protected role="tutor"><AdminTestimonials /></Protected>} />
          <Route path="/admin/enquiries" element={<Protected role="tutor"><AdminEnquiries /></Protected>} />

          <Route path="/password" element={<Protected><ChangePassword /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
